<script>
export default {
  name: 'CngSearchResult',
  props: {
    searchStr: {
      type: String,
      required: true
    },
    searchApi: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      localSearchStr: this.searchStr,
      items: [],
      pager: {},
      filter: {},
      debounceTimeout: null, // Timer-Ref
      itemTypes: [
        { value: 'all', label: 'All', count: 0 },
        { value: 'pagecontent', label: '§ Title', count: 0 },
        { value: 'company', label: '^ Company', count: 0 },
        //{ value: 'product', label: '$ Product', count: 0 },
        { value: 'session', label: '& Session', count: 0 },
        { value: 'speaker', label: '@ Speaker', count: 0 }
      ],
    };
  },

  mounted() {
    console.debug('search str:', this.localSearchStr);
    const params = this.getUrlParams();
    for (const [k, v] of Object.entries(params)) {
      if (['search', 'search_multi_match', 'page'].includes(k)) continue;
      params[k] = v;
      this.filter[k] = v;
    }
    this.localSearchStr = params.search;
    this.currentPage = params.page || 1;

    this.fetchPage(1);
  },

  methods: {
    getUrlParams() {
      const params = {};
      const search = new URLSearchParams(window.location.search);
      for (const [key, value] of search.entries()) {
        params[key] = value;
      }
      return params;
    },
    updateUrlParams(params) {
      const query = new URLSearchParams(params).toString();
      const newUrl = `${window.location.pathname}?${query}`;
      window.history.replaceState(null, '', newUrl);
    },
    fetchPage(page = 1) {
      page = parseInt(page);
      let params = this.getMultiMatchSearchParams(this.localSearchStr, { page });
      params = { ...params, ...this.filter }

      const url = `${this.searchApi}?${new URLSearchParams(params).toString()}`;

      fetch(url).then(res => res.json()).then(data => {
          this.pager = {
            previous: data.previous,
            next: data.next,
            currentPage: page,
            totalHits: data.count,
            totalPages: Math.ceil(data.count / 20)
          };
          this.items = data.results;

          this.updateItemCounts(data.facets?._filter_item_type?.item_type?.buckets || []);
          this.itemTypes[0].count = data.count;

          this.updateUrlParams(params);
        })
        .catch(err => console.error(err));
    },

    onChange() {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => {
        this.fetchPage(1);
      }, 300);
    },

    addFilter(key, value) {
      if (key == 'item_type' && value == 'all') return this.deleteFilter(key)
      this.filter[key] = value;
      this.fetchPage(1);
    },
    deleteFilter(key) {
      delete this.filter[key]
      this.fetchPage(1);
    },
    nextPage() {
      if (this.pager.next) this.fetchPage(this.pager.currentPage + 1);
    },
    previousPage() {
      if (this.pager.previous) this.fetchPage(this.pager.currentPage - 1);
    },
    getMultiMatchSearchParams(searchText, initialParams = {}) {
      let searchParams = [];
      let multiMatchParams = [];

      if (searchText) {
        const tokens = searchText.split('"');
        if (tokens.length > 1) {
          for (let tok of tokens) {
            if (!tok) continue;
            if (tok.startsWith(' ') || tok.endsWith(' ')) {
              searchParams.push(...this._getSearchParams(tok.trim()));
            } else {
              multiMatchParams.push(tok);
            }
          }
        } else {
          searchParams.push(...this._getSearchParams(searchText));
        }
      }

      const params = { ...initialParams };
      if (searchParams.length) {
        params.search = searchParams;
      }
      if (multiMatchParams.length) {
        params.search_multi_match = multiMatchParams;
      }

      return params;
    },
    _getSearchParams(text) {
      return text.split('+').map(t => t.trim()).filter(Boolean);
    },
    updateItemCounts(buckets = []) {
      for (let t of this.itemTypes) {
        const match = buckets.find(b => b.key === t.value);
        t.count = match ? match.doc_count : 0;
      }
    }
  },
};
</script>

<template>
  <div>
    <!-- Search Input -->
    <div class="d-flex align-items-center">
      <input
          v-model="localSearchStr"
          @keyup="onChange"
          @keypress="event => event.which === 13 && fetchPage()"
          class="form-control me-2"
          placeholder="Search..."
          type="text">
          <button @click="fetchPage()" class="btn btn-primary">
            <i class="fas fa-search"></i>
          </button>
    </div>
    <div class="text-muted pb-3 c-text-sm">Wrap phrase in quotation marks if needed, use + sign for multiple words.</div>

    <!-- Active Filters -->
    <div v-if="Object.keys(filter).length > 0" class="mb-3">
      <div class="d-flex flex-wrap gap-1">
        <div v-for="(value, key) in filter" :key="key" class="badge rounded-pill btn text-bg-primary">
          {{ value }}
          <span @click="deleteFilter(key)" class="">
            <i class="fas fa-times"></i>
          </span>
        </div>
      </div>
    </div>

    <!-- Item Type Filter -->
    <div class="d-flex justify-content-center gap-2 mb-3">
      <button
          v-for="itemType in itemTypes"
          :key="itemType.value"
          @click="addFilter('item_type', itemType.value)"
          class="btn btn-sm btn-dark"
          :class="['item-type-btn', { active: filter.item_type === itemType.value }]">
        {{ itemType.label }} ({{ itemType.count }})
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-4">
      <i class="fas fa-spinner fa-spin"></i> Laden...
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger">
      {{ error }}
    </div>

    <!-- Results -->
    <div v-else>
      <!-- Search Stats -->
      <div class="mb-3">
        {{ pager.totalHits }} Treffer
        <span v-if="localSearchStr">für "{{ localSearchStr }}"</span>
      </div>

      <!-- Results List -->
      <div v-for="item in items" :key="item.id" class="c-search-result">
        <a :href="item.url"><h5 class="mb-0" v-html="item.repr"></h5></a>
        <small class="text-sm text-grey">{{ item.pub_date }}</small>
        <span v-if="item.author">
          <a href="" @click.prevent="addFilter('author', item.author)"
          class="badge rounded-pill text-bg-light border text-decoration-none">@{{ item.author }}</a>
        </span>
        <span v-if="item.topic">
          <a href="" @click.prevent="addFilter('topic', item.topic)"
          class="badge rounded-pill text-bg-light border text-decoration-none">§{{ item.topic }}</a>
        </span>
        <span v-for="category in item.categories" :key="category">
          <a href="" @click.prevent="addFilter('categories', category)"
          class="badge rounded-pill text-bg-light border text-decoration-none me-1">
            {{ category }}
          </a>
        </span>
        <div class="mt-2" v-html="item.text"></div>
        <hr>
      </div>

      <!-- Pagination -->
      <div class="d-flex flex-row align-items-center justify-content-center gap-2">
        <button
            @click="previousPage()"
            :disabled="!pager.previous"
            class="btn btn-dark">
          <i class="bi bi-chevron-left"></i>
        </button>

        <span>
          {{ pager.currentPage }} / {{ pager.totalPages }}
        </span>

        <button
            @click="nextPage()"
            :disabled="!pager.next"
            class="btn btn-dark">
            <i class="bi bi-chevron-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>